import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { getSearchWithoutActionParams } from './use-filters-panel-state';

interface CheckAndExecutePendingActionOptions {
    /** The action name to match (e.g., 'addToCart', 'updateQuantity', 'removeItem') */
    actionName: string;
    /**
     * Function that determines if the pending action matches this component.
     * Receives the parsed action params from the URL and should return true if the action should execute.
     *
     * @example
     * // Match by productId
     * shouldExecute: (params) => params.productId === currentProductId
     *
     * @example
     * // Match by multiple criteria
     * shouldExecute: (params) => params.itemId === currentItemId && params.quantity === 1
     */
    shouldExecute: (actionParams: Record<string, unknown>) => boolean;
    /** Callback to execute if action matches - receives the parsed action params */
    onMatch: (actionParams: Record<string, unknown>) => void | Promise<void>;
}

/**
 * Generic helper hook that checks URL query params for pending actions and executes them if they match.
 *
 * This is used by components to execute pending actions that were initiated before authentication.
 * The component must be mounted and ready before this hook executes the action.
 *
 * @example
 * ```tsx
 * // Match by productId
 * useCheckAndExecutePendingAction({
 *   actionName: 'addToCart',
 *   shouldExecute: (params) => params.productId === currentProductId,
 *   onMatch: async (params) => {
 *     setIsLoading(true);
 *     await handleAddToCart(product);
 *   }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Match by multiple criteria
 * useCheckAndExecutePendingAction({
 *   actionName: 'updateQuantity',
 *   shouldExecute: (params) => params.itemId === currentItemId && params.variantId === currentVariantId,
 *   onMatch: async (params) => {
 *     setIsUpdating(true);
 *     await handleUpdateQuantity(params.itemId, params.quantity);
 *   }
 * });
 * ```
 */
function buildPendingActionKey(action: string, actionParams: string): string {
    return `${action}\0${actionParams}`;
}

export function useCheckAndExecutePendingAction({
    actionName,
    shouldExecute,
    onMatch,
}: CheckAndExecutePendingActionOptions) {
    const location = useLocation();
    const navigate = useNavigate();
    /** Last pending-action key handled for this hook instance (cleared when params leave the URL). */
    const executedPendingKeyRef = useRef<string | null>(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlAction = urlParams.get('action');
        const urlActionParamsStr = urlParams.get('actionParams');

        if (urlAction !== actionName || !urlActionParamsStr) {
            executedPendingKeyRef.current = null;
            return;
        }

        const pendingKey = buildPendingActionKey(actionName, urlActionParamsStr);

        if (executedPendingKeyRef.current === pendingKey) {
            return;
        }

        try {
            const urlActionParams = JSON.parse(urlActionParamsStr) as Record<string, unknown>;

            if (!shouldExecute(urlActionParams)) {
                return;
            }

            executedPendingKeyRef.current = pendingKey;

            void (async () => {
                try {
                    await onMatch(urlActionParams);
                } catch (error) {
                    if (executedPendingKeyRef.current === pendingKey) {
                        executedPendingKeyRef.current = null;
                    }
                    throw error;
                }
            })();
        } catch {
            executedPendingKeyRef.current = null;
            void navigate(
                { pathname: location.pathname, search: getSearchWithoutActionParams(location.search) },
                { replace: true }
            );
        }
    }, [actionName, shouldExecute, location.pathname, location.search, onMatch, navigate]);
}
